import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Bell, ChevronDown, ChevronRight, Info, Lock, Moon, Phone, Wrench } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtError } from "@/lib/api";
import { L } from "@/constants/labels";

// P3f — Textes de consentement SMS (wording produit, 2026-08-31).
const SMS_LABEL_TRANSACTIONNEL =
  "Recevoir par SMS les informations liées à mes chantiers : attribution d'une mission, validation d'un jalon, libération d'un paiement.";
const SMS_LABEL_PROSPECTION =
  "Recevoir par SMS les nouveaux chantiers correspondant à mon métier et à mon secteur.";
const SMS_MENTION_FOOTER =
  "Vous pouvez vous désinscrire à tout moment depuis cette page ou via le lien présent dans chaque SMS. Vos coordonnées ne sont jamais transmises à des tiers.";
const SMS_STOPPED_TEXT =
  "Vous ne recevez plus de SMS. Vous pouvez réactiver la réception à tout moment.";
const SMS_NO_PHONE_TEXT =
  "Ajoutez un numéro de téléphone à votre profil pour activer les SMS de notification.";
const SMS_SHARED_STOPPED_TEXT =
  "Ce numéro a été désinscrit des SMS depuis un autre compte. Réactivez-le pour recevoir à nouveau nos messages.";

/**
 * Préférences de notification — P3e simplifié (2026-08).
 *
 * Trois moments de vie de l'artisan (validé user) :
 *   1. "Nouveaux chantiers" — chercher du travail.
 *   2. "Mes chantiers en cours" — suivre un chantier.
 *   3. "Rappels administratifs" — tenir ses papiers à jour.
 *
 * Réglages avancés (matrice 9×3, quiet hours, cap SMS) repliés par défaut.
 * Zero UI legacy : les anciennes clés (matching_besoins, frequency) sont
 * synchronisées bidirectionnellement côté backend, l'utilisateur ne les
 * voit pas.
 *
 * Bouton "Mode calme" en haut : raccourci qui désactive tous les envois
 * NON urgents. Les urgents (mission attribuée, paiement libéré, jalon
 * validé) restent actifs — c'est explicite sous le toggle.
 */
const EVENT_LABELS = {
  chat_message_new:              "Nouveau message sur un chantier",
  candidature_recue:             "Candidature reçue (entreprise)",
  mission_attribuee:             "Chantier attribué",
  jalon_valide:                  "Jalon validé",
  paiement_libere:               "Paiement libéré",
  document_expiration_j60:       "Document qui expire dans 60 j",
  document_expiration_j30:       "Document qui expire dans 30 j",
  document_expiration_j7:        "Document qui expire dans 7 j",
  nouveau_besoin_correspondant:  "Nouveau chantier correspondant",
};
const CHANNEL_LABELS = { in_app: "In-app", email: "E-mail", sms: "SMS" };
const URGENT_EVENTS = ["mission_attribuee", "paiement_libere", "jalon_valide"];
const DOC_EVENTS = ["document_expiration_j60", "document_expiration_j30",
                     "document_expiration_j7"];
const CHANTIERS_EVENTS = ["chat_message_new", "candidature_recue",
                            "mission_attribuee", "jalon_valide", "paiement_libere"];

// ---- Dérivations UI depuis notif_prefs backend ----
function deriveNouveauxChantiers(p) {
  if (p.matching_besoins === false || p.frequency === "never") return "never";
  return p.frequency || "immediate";
}
function deriveMesChantiers(p) {
  // "email_only" | "sms_urgents" | "sms_all"
  const pe = p.per_event || {};
  const smsUrgentOn = URGENT_EVENTS.every((e) => pe[e]?.sms === true);
  const smsAllOn = CHANTIERS_EVENTS.every((e) => pe[e]?.sms === true);
  if (smsAllOn) return "sms_all";
  if (smsUrgentOn) return "sms_urgents";
  return "email_only";
}
function deriveRappelsAdmin(p) {
  const pe = p.per_event || {};
  return DOC_EVENTS.some((e) => pe[e]?.email === true);
}

// ---- Constructeurs de patch pour les 3 lignes ----
function patchNouveauxChantiers(value) {
  // value ∈ {immediate, daily, weekly, never}
  const emailOn = value !== "never";
  return {
    frequency: value,
    matching_besoins: emailOn,
    matching_prestataires: emailOn,
    per_event: {
      nouveau_besoin_correspondant: { email: emailOn, in_app: emailOn },
    },
  };
}
function patchMesChantiers(mode) {
  // mode ∈ {email_only, sms_urgents, sms_all}
  const smsForAll = mode === "sms_all";
  const smsForUrgent = mode === "sms_urgents" || smsForAll;
  const per_event = {};
  URGENT_EVENTS.forEach((e) => { per_event[e] = { email: true, sms: smsForUrgent }; });
  ["chat_message_new", "candidature_recue"].forEach((e) => {
    per_event[e] = { email: true, sms: smsForAll };
  });
  return { per_event };
}
function patchRappelsAdmin(on) {
  const per_event = {};
  DOC_EVENTS.forEach((e) => { per_event[e] = { email: !!on, in_app: false }; });
  return { per_event };
}
function patchModeCalme(on) {
  if (!on) {
    // Rétablit un preset actif — l'utilisateur ré-affinera au besoin.
    return {
      frequency: "daily",
      matching_besoins: true, matching_prestataires: true, candidature: true,
      per_event: {
        chat_message_new:              { email: true, sms: false },
        nouveau_besoin_correspondant:  { email: true, sms: false },
        candidature_recue:             { email: true, sms: true },
        mission_attribuee:             { email: true, sms: true },
        jalon_valide:                  { email: true, sms: true },
        paiement_libere:               { email: true, sms: true },
        document_expiration_j60:       { email: true, in_app: false },
        document_expiration_j30:       { email: true, in_app: false },
        document_expiration_j7:        { email: true, in_app: false },
      },
    };
  }
  // ON : coupe tout ce qui n'est pas urgent.
  return {
    frequency: "never",
    matching_besoins: false, matching_prestataires: false,
    per_event: {
      chat_message_new:              { email: false, sms: false },
      candidature_recue:             { email: false, sms: false },
      nouveau_besoin_correspondant:  { email: false, sms: false, in_app: false },
      document_expiration_j60:       { email: false },
      document_expiration_j30:       { email: false },
      document_expiration_j7:        { email: false },
      // urgents PRÉSERVÉS : mission_attribuee, jalon_valide, paiement_libere.
    },
  };
}

export default function NotifPreferences() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const load = async () => {
    try { const r = await api.get("/notifications/preferences"); setData(r.data); }
    catch (err) { toast.error(fmtError(err)); }
  };
  useEffect(() => { load(); }, []);

  const applyPatch = async (patch, msg = "Préférences enregistrées") => {
    setBusy(true);
    try {
      const r = await api.patch("/notifications/preferences", patch);
      setData((d) => ({ ...d, notif_prefs: r.data.notif_prefs }));
      toast.success(msg);
    } catch (err) { toast.error(fmtError(err)); }
    finally { setBusy(false); }
  };

  const p = data?.notif_prefs;
  const derived = useMemo(() => (p ? {
    nouveaux_chantiers: deriveNouveauxChantiers(p),
    mes_chantiers: deriveMesChantiers(p),
    rappels_admin: deriveRappelsAdmin(p),
    mode_calme: (p.frequency === "never"
                  && (p.per_event?.chat_message_new?.email === false)),
  } : null), [p]);

  if (!p) return <div className="p-6 text-sm text-[#3E4C59]">Chargement…</div>;

  const roleLabel = user?.role === "entreprise"
    ? L.wording.notifNouveauxArtisans
    : "Nouveaux chantiers correspondants";

  return (
    <div className="max-w-2xl space-y-4" data-testid="notif-preferences-page">
      {/* ---- Mode calme ---- */}
      <div className="card p-4 bg-[#F7F9FA]">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={derived.mode_calme}
            onChange={(e) => applyPatch(patchModeCalme(e.target.checked),
              e.target.checked ? "Mode calme activé" : "Mode calme désactivé")}
            disabled={busy}
            className="mt-1"
            data-testid="notif-mode-calme"
          />
          <div>
            <div className="text-sm font-medium text-[#1F2933] inline-flex items-center gap-2">
              <Moon size={14} className="text-[#3E4C59]" />
              Mode calme — je veux moins de notifications
            </div>
            <p className="text-xs text-[#3E4C59] mt-1">
              Vous serez toujours prévenu si un chantier vous est attribué,
              si un jalon est validé ou si un paiement est libéré.
              Le reste (messages, nouveaux chantiers, rappels) est coupé.
            </p>
          </div>
        </label>
      </div>

      {/* ---- 3 lignes principales ---- */}
      <div className="card p-4 space-y-5">
        <h1 className="text-xl font-semibold text-[#1F2933] inline-flex items-center gap-2">
          <Bell size={18} className="text-[#E8710A]" /> Mes notifications
        </h1>

        {/* Ligne 1 — Nouveaux chantiers */}
        <div data-testid="notif-line-nouveaux-chantiers">
          <label className="label" htmlFor="ln1">{roleLabel}</label>
          <select id="ln1" className="input"
            value={derived.nouveaux_chantiers}
            onChange={(e) => applyPatch(patchNouveauxChantiers(e.target.value))}
            disabled={busy}
            data-testid="notif-ln-nouveaux-chantiers"
          >
            <option value="immediate">Immédiat (dans les 5 minutes)</option>
            <option value="daily">Un e-mail par jour maximum</option>
            <option value="weekly">Un e-mail par semaine maximum</option>
            <option value="never">Ne pas me prévenir</option>
          </select>
        </div>

        {/* Ligne 2 — Mes chantiers en cours */}
        <div data-testid="notif-line-mes-chantiers">
          <label className="label">Mes chantiers en cours</label>
          <div className="space-y-2">
            {[
              ["email_only", "E-mail seul",
                "Vous recevez un e-mail à chaque événement."],
              ["sms_urgents", "E-mail + SMS pour les moments décisifs",
                "SMS uniquement quand un chantier vous est attribué, qu'un jalon est validé ou qu'un paiement est libéré."],
              ["sms_all", "E-mail + SMS à chaque événement",
                "Un SMS aussi à chaque nouveau message ou candidature."],
            ].map(([value, label, help]) => (
              <label key={value}
                className="flex items-start gap-2 text-sm cursor-pointer"
                data-testid={`notif-ln-mes-chantiers-${value}`}>
                <input type="radio" name="mes_chantiers"
                  className="mt-1" value={value}
                  checked={derived.mes_chantiers === value}
                  onChange={() => applyPatch(patchMesChantiers(value))}
                  disabled={busy} />
                <span>
                  <span className="font-medium text-[#1F2933]">{label}</span>
                  <span className="block text-xs text-[#3E4C59]">{help}</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Ligne 3 — Rappels administratifs */}
        <div data-testid="notif-line-rappels-admin">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox"
              checked={derived.rappels_admin}
              onChange={(e) => applyPatch(patchRappelsAdmin(e.target.checked))}
              disabled={busy}
              data-testid="notif-ln-rappels-admin" />
            <span className="text-sm text-[#1F2933]">
              Rappels administratifs (documents qui expirent bientôt)
            </span>
          </label>
          <p className="text-xs text-[#3E4C59] ml-6 mt-1 inline-flex items-start gap-1">
            <Info size={12} className="mt-0.5" />
            Trois rappels par document : 60 jours, 30 jours et 7 jours avant l'expiration.
          </p>
        </div>
      </div>

      {/* ---- Réglages avancés (replié) ---- */}
      <div className="card p-4">
        <button type="button"
          className="btn-ghost text-sm inline-flex items-center gap-2 -m-1 p-1"
          onClick={() => setShowAdvanced((x) => !x)}
          data-testid="notif-toggle-advanced"
        >
          {showAdvanced ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <Wrench size={14} />
          Réglages avancés
        </button>
        {showAdvanced && (
          <div className="mt-4 space-y-5" data-testid="notif-advanced-panel">
            <AdvancedMatrix p={p} busy={busy} onPatch={applyPatch} />
            <AdvancedQuietHours p={p} busy={busy} onPatch={applyPatch} />
          </div>
        )}
      </div>

      {/* ---- P3f : Consentement SMS ---- */}
      <SmsConsentCard />
    </div>
  );
}


function SmsConsentCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ transactionnel: false, prospection: false });

  const load = async () => {
    try {
      const r = await api.get("/notif/sms-status");
      setStatus(r.data);
      // Draft initial :
      // - jamais consenti + a un téléphone → défaut spec : trans coché, pros décoché.
      // - déjà consenti → refléter l'état stocké.
      const c = r.data.consents || {};
      if (r.data.consent_at) {
        setDraft({ transactionnel: !!c.transactionnel, prospection: !!c.prospection });
      } else {
        setDraft({ transactionnel: true, prospection: false });
      }
    } catch (err) { toast.error(fmtError(err)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!draft.transactionnel && !draft.prospection) {
      toast.error("Cochez au moins une case pour continuer, ou utilisez « Me désinscrire des SMS ».");
      return;
    }
    setBusy(true);
    try {
      await api.post("/notif/consent-sms", {
        cgu_version: status.cgu_current_version || "unknown",
        consents: draft,
      });
      toast.success("Préférences SMS enregistrées");
      await load();
    } catch (err) { toast.error(fmtError(err)); }
    finally { setBusy(false); }
  };

  const stop = async () => {
    if (!confirm("Vous ne recevrez plus de SMS. Confirmer ?")) return;
    setBusy(true);
    try {
      await api.post("/notif/stop-sms");
      toast.success("Désinscription SMS effectuée");
      await load();
    } catch (err) { toast.error(fmtError(err)); }
    finally { setBusy(false); }
  };

  if (!status) return null;

  const hasPhone = !!status.phone_e164;
  const userStopped = status.sms_stop === true;
  const sharedStopped = status.shared_number_stopped === true;
  const anyConsent = !!(status.consents?.transactionnel || status.consents?.prospection);
  const isActive = hasPhone && !userStopped && !sharedStopped && anyConsent;
  const isStopped = hasPhone && userStopped;

  return (
    <div className="card p-4 space-y-3" data-testid="sms-consent-card">
      <h3 className="text-sm font-medium text-[#1F2933] inline-flex items-center gap-2">
        <Phone size={14} className="text-[#3E4C59]" />
        Consentement SMS
      </h3>

      {!hasPhone && (
        <p className="text-xs text-[#3E4C59]" data-testid="sms-consent-no-phone">
          {SMS_NO_PHONE_TEXT}
        </p>
      )}

      {hasPhone && sharedStopped && (
        <div className="rounded-md border border-[#F1C40F] bg-[#FEF9E7] px-3 py-2 text-xs text-[#7D6608] inline-flex items-start gap-2"
             data-testid="sms-consent-shared-stopped">
          <Lock size={14} className="mt-0.5" />
          <span>{SMS_SHARED_STOPPED_TEXT}</span>
        </div>
      )}

      {hasPhone && !userStopped && !sharedStopped && (
        <p className="text-xs text-[#3E4C59]" data-testid="sms-consent-header-active">
          Numéro : <span className="font-medium">{status.phone_e164}</span>
          {status.consent_at && (
            <> · Dernier consentement : {new Date(status.consent_at).toLocaleString("fr-FR")} (CGU du {status.cgu_version || "—"})</>
          )}
        </p>
      )}

      {hasPhone && isStopped && (
        <p className="text-xs text-[#3E4C59]" data-testid="sms-consent-stopped-text">
          {SMS_STOPPED_TEXT}
        </p>
      )}

      {hasPhone && (
        <div className="space-y-2">
          <label className="flex items-start gap-2 text-xs text-[#3E4C59] cursor-pointer">
            <input type="checkbox" className="mt-0.5"
              checked={draft.transactionnel}
              onChange={(e) => setDraft((d) => ({ ...d, transactionnel: e.target.checked }))}
              disabled={busy}
              data-testid="sms-consent-transactionnel" />
            <span>{SMS_LABEL_TRANSACTIONNEL}</span>
          </label>
          <label className="flex items-start gap-2 text-xs text-[#3E4C59] cursor-pointer">
            <input type="checkbox" className="mt-0.5"
              checked={draft.prospection}
              onChange={(e) => setDraft((d) => ({ ...d, prospection: e.target.checked }))}
              disabled={busy}
              data-testid="sms-consent-prospection" />
            <span>{SMS_LABEL_PROSPECTION}</span>
          </label>
          <p className="text-[11px] text-[#6B7280] italic pt-1"
             data-testid="sms-consent-footer-mention">
            {SMS_MENTION_FOOTER}
          </p>

          <div className="flex flex-wrap gap-3 pt-2">
            <button type="button"
              className="btn-primary text-sm"
              disabled={busy}
              onClick={save}
              data-testid="sms-consent-save-btn">
              {isStopped || sharedStopped ? "Réactiver les SMS" : "Enregistrer"}
            </button>
            {isActive && (
              <button type="button"
                className="btn-ghost text-sm text-[#C0392B] underline"
                onClick={stop} disabled={busy}
                data-testid="sms-consent-stop-btn">
                Me désinscrire des SMS
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// ---- Sous-composants avancés ----
function AdvancedMatrix({ p, busy, onPatch }) {
  const pe = p.per_event || {};
  const toggle = (evt, canal, v) => onPatch({ per_event: { [evt]: { [canal]: v } } });
  return (
    <div>
      <h3 className="text-sm font-medium text-[#1F2933]">
        Par événement (in-app / e-mail / SMS)
      </h3>
      <p className="text-xs text-[#3E4C59] mt-1">
        Réglage fin par événement. Les événements urgents contournent les quiet hours.
      </p>
      <div className="mt-2 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-[#3E4C59]">
              <th className="py-2 pr-3">Événement</th>
              {Object.entries(CHANNEL_LABELS).map(([k, v]) => (
                <th key={k} className="py-2 px-2 text-center">{v}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(EVENT_LABELS).map(([evt, label]) => {
              const chosen = pe[evt] || {};
              return (
                <tr key={evt} className="border-t border-[#E4E7EB]" data-testid={`notif-row-${evt}`}>
                  <td className="py-2 pr-3 text-[#1F2933]">{label}</td>
                  {Object.keys(CHANNEL_LABELS).map((c) => (
                    <td key={c} className="py-2 px-2 text-center">
                      <input type="checkbox" checked={!!chosen[c]}
                        onChange={(e) => toggle(evt, c, e.target.checked)}
                        disabled={busy}
                        data-testid={`notif-cell-${evt}-${c}`} />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdvancedQuietHours({ p, busy, onPatch }) {
  const qh = p.quiet_hours || { enabled: true, start_hour: 20, end_hour: 8 };
  const smsCap = p.sms_daily_cap ?? 5;
  const save = (patch) => onPatch({ quiet_hours: patch });
  return (
    <div>
      <h3 className="text-sm font-medium text-[#1F2933]">Plages horaires SMS</h3>
      <p className="text-xs text-[#3E4C59] mt-1">
        Les SMS non urgents ne sont jamais envoyés en dehors du créneau — ni le
        dimanche. Les e-mails restent envoyés à toute heure.
      </p>
      <div className="mt-3 flex flex-wrap items-end gap-4">
        <label className="text-xs text-[#3E4C59]">
          <div>Début (silence)</div>
          <input type="number" min={0} max={23} value={qh.start_hour ?? 20}
            onChange={(e) => save({ start_hour: parseInt(e.target.value || "0", 10) })}
            disabled={busy} className="input mt-0.5 w-20"
            data-testid="notif-qh-start" />
        </label>
        <label className="text-xs text-[#3E4C59]">
          <div>Fin (reprise)</div>
          <input type="number" min={0} max={23} value={qh.end_hour ?? 8}
            onChange={(e) => save({ end_hour: parseInt(e.target.value || "0", 10) })}
            disabled={busy} className="input mt-0.5 w-20"
            data-testid="notif-qh-end" />
        </label>
        <label className="text-xs text-[#3E4C59] inline-flex items-center gap-2 mb-1">
          <input type="checkbox" checked={qh.enabled !== false}
            onChange={(e) => save({ enabled: e.target.checked })}
            disabled={busy} data-testid="notif-qh-enabled" />
          <span>Quiet hours activées</span>
        </label>
        <label className="text-xs text-[#3E4C59] inline-flex items-center gap-2 mb-1">
          <span>Plafond SMS / 24 h</span>
          <input type="number" min={0} max={50} value={smsCap}
            onChange={(e) => onPatch({ sms_daily_cap: parseInt(e.target.value || "0", 10) })}
            disabled={busy} className="input w-16" data-testid="notif-sms-cap" />
        </label>
      </div>
    </div>
  );
}
